import { TRUCKING_PROVIDERS } from '../../formatters/select-options';
import { RULE_PATTERN, TruckingWhitelistRule } from './rule';

/** Integrations are a closed set, so a typo is rejected rather than left to never match anything */
const canonicalIntegration = (value: string) =>
  TRUCKING_PROVIDERS.find((provider) => provider.toUpperCase() === value.toUpperCase());

/**
 * Commas are the canonical separator, but newlines and semicolons are accepted too so a long
 * whitelist can be kept one rule per line without breaking anything that already stores commas.
 */
export const splitRuleEntries = (raw: string) =>
  (raw || '')
    .split(/[,\n;]/)
    .map((entry) => entry.trim())
    .filter(Boolean);

export const parseRule = (entry: string): TruckingWhitelistRule | undefined => {
  const match = RULE_PATTERN.exec(entry);
  if (!match) return undefined;

  const [, deny, scac, broker, integration, origin, fpAccountId] = match;

  const knownIntegration = integration ? canonicalIntegration(integration) : undefined;
  if (integration && !knownIntegration) return undefined;

  return {
    raw: entry,
    isDeny: deny === '!',
    // Values stay as typed rather than normalised, so the editor shows a broker the way it's
    // spelled; matching upper-cases both sides instead.
    scac: scac === '*' ? undefined : scac,
    broker: broker?.trim() || undefined,
    // The integration is a known value, so it's stored canonically rather than as typed
    integration: knownIntegration,
    origin: origin || undefined,
    fpAccountId: fpAccountId ? Number(fpAccountId) : undefined,
  };
};

/**
 * Malformed entries come back separately rather than being dropped, so every caller can decide
 * what to do about them — the validator rejects them, the quote path logs them.
 */
export const parseRules = (raw: string) => {
  const rules: TruckingWhitelistRule[] = [];
  const invalid: string[] = [];

  for (const entry of splitRuleEntries(raw)) {
    const rule = parseRule(entry);
    if (rule) rules.push(rule);
    else invalid.push(entry);
  }

  return { rules, invalid };
};
